import * as THREE from 'three';
import { aabbOverlap, type CellHitbox } from '@shared/protocol';
import { World } from '../world/World';
import {
  WORLD_SIZE_X,
  WORLD_SIZE_Z,
  WORLD_HEIGHT,
  WORLD_MIN_Y,
} from '../world/constants';
import { Input } from './Input';
import { Puppet } from './Puppet';

const WALK_SPEED = 4.3;
const SPRINT_SPEED = 6.5;
const JUMP_VELOCITY = 8.2;
const GRAVITY = 24;
const AUTO_JUMP_HEIGHT = 1;
/** Ground/air acceleration towards the desired velocity, 1/s. */
const ACCEL_GROUND = 14;
const ACCEL_AIR = 4;

/** Fly mode (toggled by double-tapping Space): Space = up, Shift = down. */
const FLY_SPEED = 11;
const FLY_VERTICAL_SPEED = 8;
const ACCEL_FLY = 10;

const MOUSE_SENSITIVITY = 0.0023;
const MAX_PITCH = Math.PI / 2 - 0.01;

/** Player collision box: 0.6 wide, 1.8 tall, origin at the feet. */
export const PLAYER_HALF_WIDTH = 0.3;
export const PLAYER_HEIGHT = 1.8;
const EPS = 1e-3;

/** Camera distance steps: 0 = first person, otherwise third person. */
const MIN_CAMERA_DISTANCE = 0;
const MAX_CAMERA_DISTANCE = 8;
const CAMERA_ZOOM_STEP = 1;

/**
 * First/third person character controller: WASD movement with sprint and
 * jump, mouse look, and mouse-wheel switching between first and third
 * person camera. Double-tapping Space toggles fly mode (Space = up,
 * Shift = down, landing turns it off).
 */
export class PlayerController {
  readonly puppet: Puppet;

  /** Feet position. */
  readonly position: THREE.Vector3;
  private readonly velocity = new THREE.Vector3();

  private yaw = 0;
  private pitch = 0;
  private grounded = true;
  private flying = false;

  /** Current and target camera distance (smoothed). */
  private cameraDistance = 0;
  private targetCameraDistance = 0;

  constructor(
    private readonly world: World,
    private readonly input: Input,
    spawn: THREE.Vector3,
    options: { skinUrl?: string; yaw?: number; pitch?: number } = {},
  ) {
    this.puppet = new Puppet(options.skinUrl);
    this.position = spawn.clone();
    this.yaw = options.yaw ?? 0;
    this.pitch = Math.min(MAX_PITCH, Math.max(-MAX_PITCH, options.pitch ?? 0));
    this.puppet.group.position.copy(this.position);
    this.puppet.group.rotation.y = this.yaw;
    this.puppet.setHeadPitch(this.pitch);
  }

  /** Camera view angles (for network sync). */
  get view(): { yaw: number; pitch: number } {
    return { yaw: this.yaw, pitch: this.pitch };
  }

  get isFirstPerson(): boolean {
    return this.targetCameraDistance === 0;
  }

  get horizontalSpeed(): number {
    return Math.hypot(this.velocity.x, this.velocity.z);
  }

  get isFlying(): boolean {
    return this.flying;
  }

  update(dt: number, camera: THREE.PerspectiveCamera): void {
    this.updateLook();
    this.updateZoom();
    this.updateMovement(dt);
    this.updatePuppet(dt);
    this.updateCamera(dt, camera);
  }

  private updateLook(): void {
    const { dx, dy } = this.input.consumeMouseDelta();
    this.yaw -= dx * MOUSE_SENSITIVITY;
    this.pitch -= dy * MOUSE_SENSITIVITY;
    this.pitch = THREE.MathUtils.clamp(this.pitch, -MAX_PITCH, MAX_PITCH);
  }

  private updateZoom(): void {
    const wheel = this.input.consumeWheelDelta();
    if (wheel !== 0) {
      this.targetCameraDistance = THREE.MathUtils.clamp(
        this.targetCameraDistance + wheel * CAMERA_ZOOM_STEP,
        MIN_CAMERA_DISTANCE,
        MAX_CAMERA_DISTANCE,
      );
    }
  }

  private updateMovement(dt: number): void {
    if (this.input.consumeFlyToggle()) {
      this.flying = !this.flying;
      if (this.flying) this.velocity.y = 0;
    }

    // Desired horizontal velocity in world space, driven by yaw.
    const inputDir = new THREE.Vector3(
      (this.input.right ? 1 : 0) - (this.input.left ? 1 : 0),
      0,
      (this.input.backward ? 1 : 0) - (this.input.forward ? 1 : 0),
    );
    const maxSpeed = this.flying
      ? FLY_SPEED
      : this.input.sprint
        ? SPRINT_SPEED
        : WALK_SPEED;
    const desired = inputDir
      .normalize()
      .applyAxisAngle(new THREE.Vector3(0, 1, 0), this.yaw)
      .multiplyScalar(maxSpeed);

    const accel = this.flying ? ACCEL_FLY : this.grounded ? ACCEL_GROUND : ACCEL_AIR;
    const blend = 1 - Math.exp(-accel * dt);
    this.velocity.x += (desired.x - this.velocity.x) * blend;
    this.velocity.z += (desired.z - this.velocity.z) * blend;

    if (this.flying) {
      // Space rises, Shift descends; no gravity while flying.
      const desiredY =
        ((this.input.jump ? 1 : 0) - (this.input.sprint ? 1 : 0)) *
        FLY_VERTICAL_SPEED;
      this.velocity.y += (desiredY - this.velocity.y) * blend;
    } else {
      if (this.grounded && this.input.jump) {
        this.velocity.y = JUMP_VELOCITY;
        this.grounded = false;
      }
      this.velocity.y -= GRAVITY * dt;
    }

    // Axis-separated movement with voxel collision. The vertical axis goes
    // last and is where "standing on something" is detected.
    const deltaX = this.velocity.x * dt;
    const deltaZ = this.velocity.z * dt;
    const targetX = this.position.x + deltaX;
    const targetZ = this.position.z + deltaZ;
    const canAutoJump =
      !this.flying &&
      this.grounded &&
      inputDir.lengthSq() > 0;
    const blockedX = this.moveAxis('x', deltaX);
    const blockedZ = this.moveAxis('z', deltaZ);
    if (
      canAutoJump &&
      (blockedX || blockedZ) &&
      this.canOccupy(targetX, this.position.y + AUTO_JUMP_HEIGHT, targetZ)
    ) {
      this.velocity.y = JUMP_VELOCITY;
      this.grounded = false;
    }
    this.grounded = false;
    this.moveAxis('y', this.velocity.y * dt);

    // Descending onto solid ground ends fly mode.
    if (this.flying && this.grounded) this.flying = false;

    // Invisible border: clamp position to the playable area.
    const margin = 0.3;
    const clampedX = THREE.MathUtils.clamp(this.position.x, margin, WORLD_SIZE_X - margin);
    const clampedZ = THREE.MathUtils.clamp(this.position.z, margin, WORLD_SIZE_Z - margin);
    if (clampedX !== this.position.x) this.velocity.x = 0;
    if (clampedZ !== this.position.z) this.velocity.z = 0;
    this.position.x = clampedX;
    this.position.z = clampedZ;

    // Height ceiling: don't fly above the world's build limit.
    if (this.position.y > WORLD_HEIGHT) {
      this.position.y = WORLD_HEIGHT;
      this.velocity.y = Math.min(this.velocity.y, 0);
    }
  }

  /**
   * Move along one axis and resolve collisions with player colliders
   * (full cell or shrunk custom hitboxes).
   */
  private moveAxis(axis: 'x' | 'y' | 'z', delta: number): boolean {
    if (delta === 0) return false;
    this.position[axis] += delta;

    let collided = false;
    for (let iter = 0; iter < 4; iter++) {
      const box = this.playerAabb();
      const hit = this.firstOverlappingCollider(box);
      if (!hit) break;
      collided = true;
      if (axis === 'y') {
        if (delta < 0) {
          this.position.y = hit.maxY;
          this.grounded = true;
        } else {
          this.position.y = hit.minY - PLAYER_HEIGHT - EPS;
        }
        this.velocity.y = 0;
      } else if (axis === 'x') {
        this.position.x = delta > 0
          ? hit.minX - PLAYER_HALF_WIDTH - EPS
          : hit.maxX + PLAYER_HALF_WIDTH + EPS;
        this.velocity.x = 0;
      } else {
        this.position.z = delta > 0
          ? hit.minZ - PLAYER_HALF_WIDTH - EPS
          : hit.maxZ + PLAYER_HALF_WIDTH + EPS;
        this.velocity.z = 0;
      }
    }
    return collided;
  }

  /** Whether the player AABB fits at a position without touching colliders/borders. */
  private canOccupy(x: number, y: number, z: number): boolean {
    if (
      x - PLAYER_HALF_WIDTH < 0 ||
      x + PLAYER_HALF_WIDTH > WORLD_SIZE_X ||
      z - PLAYER_HALF_WIDTH < 0 ||
      z + PLAYER_HALF_WIDTH > WORLD_SIZE_Z
    ) {
      return false;
    }
    const box: CellHitbox = {
      minX: x - PLAYER_HALF_WIDTH,
      maxX: x + PLAYER_HALF_WIDTH,
      minY: y,
      maxY: y + PLAYER_HEIGHT,
      minZ: z - PLAYER_HALF_WIDTH,
      maxZ: z + PLAYER_HALF_WIDTH,
    };
    return this.firstOverlappingCollider(box) === null;
  }

  private playerAabb(): CellHitbox {
    return {
      minX: this.position.x - PLAYER_HALF_WIDTH,
      maxX: this.position.x + PLAYER_HALF_WIDTH,
      minY: this.position.y,
      maxY: this.position.y + PLAYER_HEIGHT,
      minZ: this.position.z - PLAYER_HALF_WIDTH,
      maxZ: this.position.z + PLAYER_HALF_WIDTH,
    };
  }

  private firstOverlappingCollider(box: CellHitbox): CellHitbox | null {
    for (let bx = Math.floor(box.minX); bx <= Math.floor(box.maxX - EPS); bx++) {
      for (let by = Math.floor(box.minY); by <= Math.floor(box.maxY - EPS); by++) {
        for (let bz = Math.floor(box.minZ); bz <= Math.floor(box.maxZ - EPS); bz++) {
          const collider = this.world.getPlayerCollider(bx, by, bz);
          if (collider && aabbOverlap(box, collider, EPS)) return collider;
        }
      }
    }
    return null;
  }

  private updatePuppet(dt: number): void {
    this.puppet.group.position.copy(this.position);
    this.puppet.group.rotation.y = this.yaw;
    this.puppet.setHeadPitch(this.pitch);
    this.puppet.update(this.horizontalSpeed, this.grounded, dt);
    this.puppet.setVisible(this.cameraDistance > 0.6);
  }

  private updateCamera(dt: number, camera: THREE.PerspectiveCamera): void {
    // Smooth the zoom transition.
    const blend = 1 - Math.exp(-12 * dt);
    this.cameraDistance +=
      (this.targetCameraDistance - this.cameraDistance) * blend;
    if (Math.abs(this.cameraDistance - this.targetCameraDistance) < 0.01) {
      this.cameraDistance = this.targetCameraDistance;
    }

    const eye = new THREE.Vector3(
      this.position.x,
      this.position.y + Puppet.EYE_HEIGHT,
      this.position.z,
    );

    const lookDirection = new THREE.Vector3(
      -Math.sin(this.yaw) * Math.cos(this.pitch),
      Math.sin(this.pitch),
      -Math.cos(this.yaw) * Math.cos(this.pitch),
    );

    if (this.cameraDistance <= 0.01) {
      camera.position.copy(eye);
    } else {
      // Third person: pull back along the view ray, but not below world depth.
      const cameraPos = eye
        .clone()
        .addScaledVector(lookDirection, -this.cameraDistance);
      cameraPos.y = Math.max(cameraPos.y, WORLD_MIN_Y + 0.2);
      camera.position.copy(cameraPos);
    }
    camera.lookAt(eye.clone().addScaledVector(lookDirection, 10));
  }
}
